import { DimensionId, DIMENSION_COLORS, DIMENSION_ORDER } from './dimensionMeta';
import { DevelopmentComposite } from './developmentScore';
import { UnifiedContent, Intervention } from './contentLoader';
import { rankInterventions, topPendulumTagIds } from './personalization';
import { ScoringResult } from './scoringEngine';
import { interventionContribution, InterventionContribution, makeTaskId } from './xp';

export interface RoadmapTask {
  /**
   * Month-scoped slot id (`<intervention.id>__m<monthIndex>`). Two slots in
   * different months produced from the same intervention have DIFFERENT ids,
   * so completing one doesn't auto-complete the other - the user has to
   * redo the technique each month, which is the whole point of the cyclic
   * roadmap.
   */
  id: string;
  /** Underlying intervention id (without month suffix). */
  interventionId: string;
  task: string;
  /** Backwards-compatible short body (== `how`). Older renderers read this. */
  description: string;
  /** One-line hook from `instructions.what` (shown under the title; full steps stay behind More). */
  summary: string;
  /** Concrete actions the user actually performs. */
  how: string;
  /** Long-form rationale: WHY this task works. Falls back to research[0]. */
  why: string;
  time: string;
  research_backing?: string;
  dimensionId?: string;
  dimensionColor?: string;
  /** Why this task was picked, surfaced from the personalization scorer. */
  reasons?: string[];
  /** Personalization score (0..N). Higher = stronger fit. */
  fitScore?: number;
  /** XP this task awards on each completion (dim/tag/pendulum/total). */
  contribution?: InterventionContribution;
}

function normLeadKey(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[\u2018\u2019`']/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/\.+$/g, '')
    .trim();
}

/**
 * One line under the task title for collapsed cards.
 * Uses `instructions.what` only when it is not a trivial repeat of the title
 * (in our catalog `what` often mirrors `name`). Otherwise returns the opening
 * of `how` so users still see a concrete hook before "More".
 */
export function taskLeadPreview(task: Pick<RoadmapTask, 'task' | 'summary' | 'how'>): string {
  const title = (task.task || '').trim();
  const sum = (task.summary || '').trim();
  const how = (task.how || '').trim();

  if (sum && normLeadKey(sum) !== normLeadKey(title)) {
    return sum;
  }

  if (!how || normLeadKey(how) === normLeadKey(title)) {
    return '';
  }

  const punctEnd = how.search(/[.!?](\s|$)/);
  if (punctEnd >= 40 && punctEnd <= 380) {
    return how.slice(0, punctEnd + 1).trim();
  }

  if (how.length <= 240) return how;

  const cut = how.slice(0, 240);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > 50 ? cut.slice(0, sp) : cut).trim()}…`;
}

export interface RoadmapMetric {
  metric: string;
  baseline: string;
  target: string;
}

/**
 * Historically called "phase" - now represents a *month* in the user's
 * 12-month cyclic roadmap. Renamed conceptually but kept as an interface so
 * the old import path still resolves cleanly across components.
 */
export interface RoadmapPhase {
  /** 1-based month index (1..12). */
  index: number;
  /** Stable id from content.months[i].id (e.g. "audit", "reset"). */
  monthId: string;
  title: string;
  tagline: string;
  /**
   * Long-form description of what this month is for. Pulled from
   * `content.months[i].narrative` so it stays editable in content.
   */
  narrative: string;
  /**
   * Legacy fields kept so older renderers don't crash. New code prefers
   * `narrative`.
   */
  focus: string;
  why_now: string;
  color: string;
  /** Dimensions emphasized this month (derived from the picked tasks). */
  dimensions: DimensionId[];
  tasks: RoadmapTask[];
  metrics: RoadmapMetric[];
  research_summary: string;
}

export type RoadmapMonth = RoadmapPhase;

// =============================================================================
// 12-MONTH CYCLIC, PERSONALIZED ROADMAP
//
// Why cyclic?
//   The intervention catalog (~60 techniques) is finite, but behavior change
//   is iterative - the same micro-habit pays back differently in month 2 vs.
//   month 8. So instead of a 30-task linear program, we give the user a
//   continuous 12-month system: 20 tasks per month, with the same tasks
//   surfacing again in later months under a new monthly theme.
//
// How tasks get picked per month:
//   1. Rank ALL interventions ONCE against the user's deficits.
//   2. Slide a window of `tasksPerMonth` (default 20) over that ranked list,
//      advancing `WINDOW_STEP` ranks per month and wrapping at the end.
//      → Month 1 = ranks 1..20 (the highest-leverage stuff).
//      → Month 2 = ranks 6..25  (mostly the same, with some fresh tasks).
//      → …
//      → Month 12 wraps around so the cycle is genuinely circular.
//   3. Tasks therefore intentionally repeat across months - that repetition
//      is the *practice*, not a bug.
//
// Re-runs cheaply on every dashboard load → fresh scores → fresh roadmap.
// To force a reset of the daily-focus lock, call `clearRoadmapState(userId)`
// or visit /dashboard?recalc=1.
// =============================================================================

const DEFAULT_TASKS_PER_MONTH = 20;
const WINDOW_STEP = 5;

/**
 * Pick the most useful "why this works" string for a task. Authors fill
 * `instructions.why` for some interventions; for the rest we fall back to the
 * first published research finding so every slot has a rationale instead of an
 * empty paragraph.
 */
function deriveWhy(iv: Intervention): string {
  const authored = (iv.instructions?.why || '').trim();
  if (authored) return authored;
  const researchFinding = iv.researches?.[0]?.finding?.trim();
  return researchFinding || '';
}

function interventionToTask(
  iv: Intervention,
  monthIndex: number,
  fitScore: number,
  reasons: string[],
  dimensionColor: string,
  content: UnifiedContent,
): RoadmapTask {
  const how = iv.instructions.how || '';
  const why = deriveWhy(iv);
  const summary = (iv.instructions.what || '').trim();
  return {
    id: makeTaskId(iv.id, monthIndex),
    interventionId: iv.id,
    task: iv.name,
    description: how,
    summary,
    how,
    why,
    time: iv.instructions.time_label || '',
    research_backing: iv.researches[0]?.finding,
    dimensionId: iv.primary_dimension,
    dimensionColor,
    fitScore,
    reasons,
    contribution: interventionContribution(iv, content),
  };
}

export interface BuildRoadmapOptions {
  /** Number of tasks each month surfaces. Defaults to 20. */
  tasksPerMonth?: number;
}

/**
 * Build the 12-month, 240-slot roadmap for the user. Months come from
 * `content.months` so naming/narrative is editable in content.
 */
export function buildPersonalRoadmap(
  scores: ScoringResult,
  content: UnifiedContent,
  composite: DevelopmentComposite,
  opts: BuildRoadmapOptions = {},
): RoadmapPhase[] {
  const tasksPerMonth = opts.tasksPerMonth ?? DEFAULT_TASKS_PER_MONTH;
  // Only prescribe from directions the user has actually taken. An untaken
  // direction has no scores behind it, so its exercises would be recommended on
  // no evidence — and they would crowd out the ones that were earned.
  const measuredDimensions = new Set(Object.keys(scores.dimensions));
  const allInterventions = Object.values(content.interventions).filter(
    (iv) => measuredDimensions.size === 0 || measuredDimensions.has(iv.primary_dimension),
  );
  if (allInterventions.length === 0 || content.months.length === 0) return [];

  const topPendTags = topPendulumTagIds(scores, content.pendulum_axes.axes);

  const ranked = rankInterventions(allInterventions, scores, {
    weakestDimensionId: composite.weakest.id,
    topPendulumTagIds: topPendTags,
  });

  const N = ranked.length;
  const window = Math.min(tasksPerMonth, N);

  return content.months.map((monthSpec, idx) => {
    const start = (idx * WINDOW_STEP) % N;
    const slice: typeof ranked = [];
    for (let k = 0; k < window; k += 1) {
      slice.push(ranked[(start + k) % N]);
    }

    const monthIndex = idx + 1;
    const tasks: RoadmapTask[] = slice.map((r) => {
      const dimColor = content.dimensions[r.intervention.primary_dimension]?.color
        || DIMENSION_COLORS[r.intervention.primary_dimension as DimensionId]
        || '#3B82F6';
      return interventionToTask(r.intervention, monthIndex, r.score, r.reasons, dimColor, content);
    });

    const dimsTouched = Array.from(new Set(
      tasks.map((t) => t.dimensionId).filter(Boolean) as DimensionId[],
    )).sort((a, b) => DIMENSION_ORDER.indexOf(a) - DIMENSION_ORDER.indexOf(b));

    // Cycle through the dimension palette so each month gets a distinct accent.
    const accent = DIMENSION_COLORS[DIMENSION_ORDER[idx % DIMENSION_ORDER.length]];

    return {
      index: idx + 1,
      monthId: monthSpec.id,
      title: monthSpec.label,
      tagline: monthSpec.tagline,
      narrative: monthSpec.narrative,
      focus: monthSpec.tagline,
      why_now: monthSpec.narrative,
      color: accent,
      dimensions: dimsTouched,
      tasks,
      metrics: [],
      research_summary: '',
    };
  });
}

// =============================================================================
// TODAY'S FOCUS - one task per day, drawn from the user's CURRENT active
// month, locked for the day so it stays stable across reloads.
// Storage: localStorage `doyourstep.dailyFocus.<userId>` → { date, taskId }
// =============================================================================

export interface TodayFocus {
  taskId: string;
  task: string;
  description: string;
  /** One-line from intervention `what` (preferred under-title copy). */
  summary: string;
  /** Concrete actions for the user to perform. */
  how: string;
  /** Long-form rationale: WHY the task works (prose, not just citation). */
  why: string;
  time: string;
  research?: string;
  dimensionId: DimensionId;
  dimensionColor: string;
  rationale: string;
  /** XP preview for this task (so the focus card can show contribution chips). */
  contribution?: InterventionContribution;
}

const DAILY_FOCUS_KEY = 'doyourstep.dailyFocus';
const STORAGE_KEY = 'doyourstep.roadmap.completed';

function todayKey(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function readDailyFocus(userId: string): { date: string; taskId: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${DAILY_FOCUS_KEY}.${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.date && parsed?.taskId) return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeDailyFocus(userId: string, taskId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      `${DAILY_FOCUS_KEY}.${userId}`,
      JSON.stringify({ date: todayKey(), taskId }),
    );
  } catch {
    /* no-op */
  }
}

// Deterministic hash → integer, used to seed daily picks even before the
// localStorage write happens (so first paint matches the persisted choice).
function hashStr(s: string): number {
  return Array.from(s).reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 2147483647, 7);
}

function activeMonth(months: RoadmapPhase[], completed: Set<string>): RoadmapPhase {
  return months.find((m) => m.tasks.some((t) => !completed.has(t.id))) || months[0];
}

export function pickTodayFocus(
  composite: DevelopmentComposite,
  months: RoadmapPhase[],
  content: UnifiedContent,
  userId: string | undefined,
  completed: Set<string>,
): TodayFocus | null {
  if (!months || months.length === 0) return null;

  const month = activeMonth(months, completed);
  if (!month || month.tasks.length === 0) return null;

  const stored = userId ? readDailyFocus(userId) : null;
  let task = stored?.date === todayKey()
    ? month.tasks.find((t) => t.id === stored.taskId)
    : undefined;

  if (!task) {
    const candidates = month.tasks.filter((t) => !completed.has(t.id));
    const pool = candidates.length > 0 ? candidates : month.tasks;
    const seed = hashStr(`${userId || 'anon'}:${todayKey()}`);
    task = pool[seed % pool.length];
    if (userId && task) writeDailyFocus(userId, task.id);
  }

  if (!task) return null;

  const targetDim = (task.dimensionId as DimensionId) || (composite.weakest.id as DimensionId);
  const dimMeta = content.dimensions[targetDim];
  const rationale = dimMeta?.level_descriptions?.low?.priority_message
    || `Today's leverage point: a focused move in ${dimMeta?.label || targetDim}.`;

  const underTitle = taskLeadPreview(task);

  return {
    taskId: task.id,
    task: task.task,
    description: task.description,
    summary: underTitle,
    how: task.how,
    why: task.why,
    time: task.time,
    research: task.research_backing,
    dimensionId: targetDim,
    dimensionColor: task.dimensionColor || '#3B82F6',
    rationale: underTitle || rationale,
    contribution: task.contribution,
  };
}

// =============================================================================
// PROGRESS TRACKING + STATE RESET
// =============================================================================

export function loadCompletedTasks(userId: string | undefined): Set<string> {
  if (!userId || typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}.${userId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function saveCompletedTasks(userId: string | undefined, ids: Set<string>): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${STORAGE_KEY}.${userId}`, JSON.stringify(Array.from(ids)));
  } catch {
    /* no-op */
  }
}

/**
 * Reset the per-day "Today's Focus" lock so the next render picks a fresh
 * task from the (possibly rebuilt) roadmap. Intentionally does NOT touch the
 * completed-task set - completions are keyed by month-scoped slot id
 * (`<intervention.id>__m<monthIndex>`), so any slot the user has already
 * finished stays finished if its (intervention, month) pair survives into the
 * rebuilt roadmap. Triggered by the "Recalculate" button or `?recalc=1`.
 */
export function clearRoadmapState(userId: string | undefined): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    localStorage.removeItem(`${DAILY_FOCUS_KEY}.${userId}`);
  } catch {
    /* no-op */
  }
}
